// @givmo/app-charity — tolerant decode for the back-office enrollment responses (CBO-IN3).
//
// Every response this feature reads arrives as `unknown` (the routes post-date the committed OpenAPI
// snapshot — see `enrollment-transport.ts`), so this module is the whole type boundary. It never
// throws: an unreadable body decodes to the empty/`null` answer and the screen renders its own state
// rather than crashing on a shape this build did not expect.
//
// ══ THE TWO RULES THAT MAKE THIS SAFE RATHER THAN MERELY TYPE-QUIET ════════════════════════════
//
//   1. **Default to what the BACKEND declares, never to a neutral zero that looks similar.** Every
//      optional field on this surface is declared `| null` server-side, so `null` IS the wire's own
//      default and matching it makes the tolerant reader and the wire AGREE rather than merely not
//      crash. Nothing here substitutes an empty string, a zero or a `false` for an absent value.
//   2. **Never let an unreadable value resolve towards the confident answer.** A status this build
//      does not recognize decodes to `null`, not to the nearest familiar member — because the one
//      thing worse than an unlabelled record is a record confidently labelled as something it is
//      not. `enrollment-vocab.ts` renders that `null` as an honest "not recognized" state.
//
// ⚠ There is deliberately NO boolean reader here, and its absence is a fact about the contract
// rather than an omission: the charity-facing LIFECYCLE models carry no boolean field at all. The one
// boolean on the enrollment surface (the second-home copy flag) lives only on the operator models,
// which this app never reads. If a boolean ever lands here, test it `=== true` — a string, number or
// object arriving on a boolean is a shape this build does not understand, and reading it as "yes"
// guesses in the direction that shows an organization something that has not happened. (The intake
// doors DO carry booleans; `enrollment-backfill-decode.ts` reads them by exactly that rule.)

import { isGivmoError } from '@givmo/api-client';
import { extractList } from '@givmo/api-client/list';

/** The record's lifecycle states, exactly as the backend spells them. */
export const ENROLLMENT_STATUSES = [
  'draft',
  'submitted',
  'approved',
  'provisioned',
  'rejected',
  'withdrawn',
] as const;
export type EnrollmentStatus = (typeof ENROLLMENT_STATUSES)[number];

/** The entity classes. Only `charity` is reachable from this console; `for_profit` is operator-made. */
export const ENTITY_CLASSES = ['charity', 'for_profit'] as const;
export type EntityClass = (typeof ENTITY_CLASSES)[number];

/**
 * The recorded per-tenant backfill decision.
 *
 * ⚠ RECORDED ONCE, AND THE SURFACE THAT RECORDS IT NEVER OFFERS TO CHANGE IT (CBO-IN4;
 * `enrollment-backfill-page.tsx`). The two options are different shapes rather than two settings of
 * one dial — a cut-in states a position as at a date, a history carry replays the transactions
 * behind one — so the backend answers a DIFFERENT second choice with a 409 and treats correcting it
 * as operator work. A control that let someone flip between them would misrepresent what changing it
 * costs.
 */
export const BACKFILL_CHOICES = ['opening_balances', 'full_history'] as const;
export type BackfillChoice = (typeof BACKFILL_CHOICES)[number];

/**
 * The event-log actions — the lifecycle's own, the two the provisioning protocol writes, and the
 * five the intake doors write (CBO-IN4).
 *
 * The door events land on the SAME log as the lifecycle ones, which is why they are decoded here
 * rather than in the backfill module: one record has one event log, and a build that could not read
 * half of it would render a record's history with silent holes in it.
 */
export const ENROLLMENT_ACTIONS = [
  'created',
  'updated',
  'submitted',
  'approved',
  'provisioned',
  'rejected',
  'withdrawn',
  'store_copy_recorded',
  'provision_redriven',
  'backfill_choice_recorded',
  'entity_map_recorded',
  'import_run_started',
  'import_run_bound',
  'import_run_refused',
] as const;
export type EnrollmentAction = (typeof ENROLLMENT_ACTIONS)[number];

/** One enrollment record, as this console reads it. */
export interface EnrollmentRecord {
  id: string;
  entityClass: EntityClass | null;
  status: EnrollmentStatus | null;
  legalEntityName: string | null;
  legalEntityEin: string | null;
  legalEntityJurisdiction: string | null;
  /**
   * The month the organization's financial year ends in, 1–12, or `null` while nobody has chosen.
   *
   * ⚠ `null` IS the wire's own answer for an unchosen convention, and it must not be rendered as
   * December. December is what a record settles to at submission, which is a different statement
   * from one the organization made — and the difference matters because this value decides what a
   * year-end means on the books it opens.
   */
  fiscalYearEndMonth: number | null;
  backfillChoice: BackfillChoice | null;
  /** The recorded rejection or withdrawal reason. Rendered verbatim; never paraphrased. */
  dispositionNote: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  submittedAt: string | null;
  approvedAt: string | null;
  provisionedAt: string | null;
  rejectedAt: string | null;
  withdrawnAt: string | null;
}

/** One entry in a record's event log. */
export interface EnrollmentEvent {
  id: string;
  action: EnrollmentAction | null;
  /** The transition this event recorded, when it carried one. */
  fromStatus: EnrollmentStatus | null;
  toStatus: EnrollmentStatus | null;
  createdAt: string | null;
}

/** A record plus its event log, oldest event first. */
export interface EnrollmentDetail extends EnrollmentRecord {
  events: EnrollmentEvent[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * A non-empty string, or `null`.
 *
 * The STRICTER of the two tolerant-read rules, following `readMessageFraming`'s precedent: only a
 * genuine non-empty string counts, and every truthy non-string (a number, an object, an array, a
 * function) reads as absent rather than being coerced. A coerced `[object Object]` in a legal-name
 * field would render as though the organization had typed it.
 */
function readString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** A member of `allowed`, or `null` for anything this build does not recognize. */
function readMember<T extends string>(value: unknown, allowed: readonly T[]): T | null {
  if (typeof value !== 'string') return null;
  return (allowed as readonly string[]).includes(value) ? (value as T) : null;
}

/**
 * A whole month number 1–12, or `null`.
 *
 * As strict as `readString` and for the same reason: `"6"` is a string that HAPPENS to look like a
 * month, and coercing it would let a wire shape this build does not understand render as a
 * convention the organization chose. Out-of-range values, fractions, `NaN` and infinities are all
 * `null` — a fiscal year does not end in month 13, and a screen that showed one would be rendering a
 * defect as a fact about the organization.
 */
export function readMonth(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isInteger(value)) return null;
  return value >= 1 && value <= 12 ? value : null;
}

/** Decode one record. Returns `null` when it carries no usable id — an unaddressable row. */
export function readEnrollmentRecord(value: unknown): EnrollmentRecord | null {
  if (!isRecord(value)) return null;
  // A record with no id cannot be linked to, refreshed, or acted on. Rendering it would offer
  // controls whose every call would be built against `undefined`.
  const id = readString(value.id);
  if (id === null) return null;

  return {
    id,
    entityClass: readMember(value.entity_class, ENTITY_CLASSES),
    status: readMember(value.status, ENROLLMENT_STATUSES),
    legalEntityName: readString(value.legal_entity_name),
    legalEntityEin: readString(value.legal_entity_ein),
    legalEntityJurisdiction: readString(value.legal_entity_jurisdiction),
    fiscalYearEndMonth: readMonth(value.fiscal_year_end_month),
    backfillChoice: readMember(value.backfill_choice, BACKFILL_CHOICES),
    dispositionNote: readString(value.disposition_note),
    createdAt: readString(value.created_at),
    updatedAt: readString(value.updated_at),
    submittedAt: readString(value.submitted_at),
    approvedAt: readString(value.approved_at),
    provisionedAt: readString(value.provisioned_at),
    rejectedAt: readString(value.rejected_at),
    withdrawnAt: readString(value.withdrawn_at),
  };
}

/** Decode one event-log entry. Returns `null` when it carries no usable id. */
export function readEnrollmentEvent(value: unknown): EnrollmentEvent | null {
  if (!isRecord(value)) return null;
  const id = readString(value.id);
  if (id === null) return null;

  // `detail` is an open-shaped object; only the two transition keys are read, and only when they name
  // states this build knows. Everything else in it is the backend's own record, not this app's.
  const detail = isRecord(value.detail) ? value.detail : {};
  return {
    id,
    action: readMember(value.action, ENROLLMENT_ACTIONS),
    fromStatus: readMember(detail.from_status, ENROLLMENT_STATUSES),
    toStatus: readMember(detail.to_status, ENROLLMENT_STATUSES),
    createdAt: readString(value.created_at),
  };
}

/**
 * Decode the record-with-events detail response.
 *
 * ⚠ An unreadable `events` decodes to `[]`, and the screen must render NOTHING for an empty log
 * rather than "no activity recorded". Every real record has at least a `created` event, so an empty
 * array here means this client could not read the log — and "no activity recorded" would state as a
 * fact about the organization something that is only a fact about the decode.
 */
export function readEnrollmentDetail(value: unknown): EnrollmentDetail | null {
  const record = readEnrollmentRecord(value);
  if (record === null) return null;
  const rawEvents = isRecord(value) ? value.events : undefined;
  const events = (Array.isArray(rawEvents) ? rawEvents : [])
    .map(readEnrollmentEvent)
    .filter((event): event is EnrollmentEvent => event !== null);
  return { ...record, events };
}

/**
 * Decode the listing.
 *
 * `extractList` reads BOTH the bare array (what the response middleware leaves after unwrapping the
 * `{data, meta, links}` envelope) and the un-unwrapped envelope, so this is correct whether or not
 * the middleware fired. Rows that decode to `null` are dropped rather than rendered as blanks.
 */
export function extractEnrollments(body: unknown): EnrollmentRecord[] {
  return extractList<unknown>(body)
    .map(readEnrollmentRecord)
    .filter((record): record is EnrollmentRecord => record !== null);
}

/**
 * The organization's LIVE record, if it has one.
 *
 * "Live" is the backend's own meaning — the states a second record would collide with (409). A
 * rejected or withdrawn record is history: it is worth showing, and it does not stop the
 * organization starting again.
 */
const LIVE_STATUSES: readonly EnrollmentStatus[] = [
  'draft',
  'submitted',
  'approved',
  'provisioned',
];

export function findLiveEnrollment(records: readonly EnrollmentRecord[]): EnrollmentRecord | null {
  // The listing arrives newest first, so the first match is the current one.
  return records.find((r) => r.status !== null && LIVE_STATUSES.includes(r.status)) ?? null;
}

/**
 * Whether this console offers to edit a record's details.
 *
 * ⚠ NARROWER THAN THE BACKEND ON PURPOSE — do not "fix" this to match. The backend's editable window
 * is `{draft, submitted}`; this console stops at `draft`. The reason is what an edit to a SUBMITTED
 * record would buy: re-submitting a submitted record is a replay that returns the current state
 * without re-evaluating any precondition, so a changed legal name or EIN could not move a stuck
 * record along. Offering the edit would imply otherwise. The path that does work — withdraw and
 * start again — is the one the record page offers, and it is offered on exactly the states the
 * backend still allows it on.
 */
export function isEditable(record: EnrollmentRecord | null): boolean {
  return record?.status === 'draft';
}

/** Whether the state machine still allows a withdrawal. Approved and later records refuse it. */
export function canWithdraw(record: EnrollmentRecord | null): boolean {
  return record?.status === 'draft' || record?.status === 'submitted';
}

/**
 * Whether setup finished — the books exist.
 *
 * ⚠ SETUP, NOT DATA. `provisioned` means the organization's entity and books were created, which is
 * the whole of what this flow promises. It says nothing about how much history has been brought in;
 * that is an open-ended surface afterwards and its own separate records.
 */
export function isSetUp(record: EnrollmentRecord | null): boolean {
  return record?.status === 'provisioned';
}

/**
 * Whether a 404 came back — which the screens render as "not available here".
 *
 * ⚠ THIS CANNOT TELL YOU WHICH OF TWO THINGS HAPPENED, and the copy must not pretend otherwise.
 * Either gate being off and a record that does not exist in the caller's scope answer with
 * byte-identical bodies, deliberately: that is what stops the console disclosing that a surface
 * exists somewhere. So callers say "not available here" rather than "something went wrong" (which
 * would invite a retry that cannot work) — but on a per-record read they must ALSO not say "the
 * module is off", because a stale bookmark or an org switch reaches this branch with the module
 * fully on. The landing page's listing read is the one place the inference is sound: a 404 on the
 * organization's OWN collection can only be the gate.
 */
export function isModuleUnavailable(error: unknown): boolean {
  return isGivmoError(error) && error.status === 404;
}

/** Whether the organization already holds a live record — a second one is refused. */
export function isAlreadyEnrolled(error: unknown): boolean {
  return isGivmoError(error) && error.status === 409;
}

/** The backend's own sentence when it wrote one, else the caller's fallback. */
export function serverMessage(error: unknown, fallback: string): string {
  return isGivmoError(error) && error.message.trim().length > 0 ? error.message : fallback;
}

/**
 * Render a wire timestamp as a date.
 *
 * Never throws and never renders "Invalid Date": an unparseable or absent value renders as an empty
 * string, and the callers omit the line entirely rather than showing a date-shaped blank.
 */
export function displayEnrollmentDate(value: string | null): string {
  if (value === null) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
